// DEPENDENCIES
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";

// CSS
import "./ProjectHealthDashboard.css";

// MUI ICONS
import CheckIcon from "@mui/icons-material/Check";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LaunchIcon from "@mui/icons-material/Launch";

// MUI COMPONENTS
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";

// API
import { getAPICall, postAPICall } from "../../config/apiCalls";
import { UserContext } from "../../context/UserContext";

// â"€â"€ Constants â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const ACTIVITY_POLL_MS = 60 * 60 * 1000;
const OFFICE_ALL      = "__all_offices__";
const MANAGER_ALL     = "__all_managers__";
const STATUS_ALL      = "__all_statuses__";

const AMEND_GRAY   = "rgba(0,0,0,0.38)";
const MS_PER_DAY   = 24 * 60 * 60 * 1000;
const addDays      = (d, n) => new Date(d.getTime() + n * MS_PER_DAY);
const fmtDate      = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const parseLocalDate = (value) => {
	if (!value) return null;
	const datePart = String(value).split(/[T ]/)[0];
	const parts = datePart.split("-");
	if (parts.length === 3) {
		const d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
		return isNaN(d.getTime()) ? null : d;
	}
	const d = new Date(String(value));
	return isNaN(d.getTime()) ? null : d;
};

const COLORS = {
	green:   "#2e7d32",
	red:     "#c62828",
	yellow:  "#ca8a04",
	missing: "#9ca3af",
	yes:     AMEND_GRAY,
	no:      AMEND_GRAY,
};

const BG_COLORS = {
	[`#2e7d32`]: "#e8f5e9",
	[`#c62828`]: "#ffebee",
	[`#ca8a04`]: "#fffde7",
	[`#9ca3af`]: "#f5f5f5",
};

// â"€â"€ Status cell sub-components â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const StatusDot = ({ label, tooltip }) => {
	const inner = <span className="phdStatusLabel">{label}</span>;
	return tooltip ? <Tooltip title={tooltip} arrow>{inner}</Tooltip> : inner;
};

const BudgetCell = ({ hasBudget, loading }) => {
	if (loading) return <span className="phdMuted">-</span>;
	if (hasBudget === null) return <span className="phdMuted">-</span>;
	return hasBudget
		? <StatusDot color={COLORS.green} label="Set"     tooltip="Budget has been set in BQE" />
		: <StatusDot color={COLORS.red}   label="None" tooltip="No budget found in BQE" />;
};

// status/amounts come from the server (classifyBudgetStatus) — all logged time
// at budget rates plus posted expenses vs. the budget, shared with Phase Reports.
const fmtMoney = (v) => `$${Math.round(Math.abs(v)).toLocaleString()}`;

const BudgetStatusCell = ({ hasBudget, budgetHours, spentHours, budgetAmount, spentAmount, status, varianceAmount, loading }) => {
	if (loading) return <span className="phdMuted">-</span>;
	if (!hasBudget || budgetAmount == null || !status || status === "Unknown") return <span className="phdMuted">-</span>;
	const detail = `${fmtMoney(spentAmount)} of ${fmtMoney(budgetAmount)} budget used · ${spentHours.toFixed(1)} of ${(budgetHours ?? 0).toFixed(1)} billable hrs`;
	if (status === "On Budget") {
		return <StatusDot color={COLORS.green} label="On Budget" tooltip={detail} />;
	}
	const isOver = status === "Over Budget";
	const tooltip = `${fmtMoney(varianceAmount ?? 0)} ${isOver ? "over" : "remaining"} (${detail})`;
	return isOver
		? <StatusDot color={COLORS.red}   label="Over"  tooltip={tooltip} />
		: <StatusDot color={COLORS.green} label="Under" tooltip={tooltip} />;
};

const AmendmentsCell = ({ submittedCount, pendingCount }) => {
	const tip = pendingCount > 0
		? `${submittedCount} submitted, ${pendingCount} pending`
		: submittedCount > 0
			? `${submittedCount} submitted amendment${submittedCount !== 1 ? "s" : ""}`
			: "No amendments";
	return (
		<Tooltip title={tip} arrow>
			<span className="phdStatusLabel">{submittedCount}</span>
		</Tooltip>
	);
};

const InvoicesCell = ({ status, overdueCount, unpaidCount, draftCount }) => {
	// Drafts never affect the health color — they aren't outstanding invoices yet —
	// but we still surface the count so they aren't invisible.
	const draftNote = draftCount > 0 ? ` (+${draftCount} draft${draftCount !== 1 ? "s" : ""})` : "";
	if (status === "no_invoices")
		return <StatusDot color={COLORS.green} label="None" tooltip={`No invoices on record${draftNote}`} />;
	if (status === "all_paid")
		return <StatusDot color={COLORS.green} label="All paid" tooltip={`No outstanding invoices${draftNote}`} />;
	if (status === "overdue") {
		const tip = `${overdueCount} overdue${unpaidCount > overdueCount ? `, ${unpaidCount - overdueCount} more pending` : ""}${draftNote}`;
		return <StatusDot color={COLORS.red} label={`${overdueCount} overdue`} tooltip={tip} />;
	}
	return (
		<StatusDot
			color={COLORS.yellow}
			label={`${unpaidCount} pending`}
			tooltip={`${unpaidCount} pending invoice${unpaidCount !== 1 ? "s" : ""}, none overdue${draftNote}`}
		/>
	);
};

const DurationCell = ({ cfStartDate, cfDurationWeeks }) => {
	const hasStart    = Boolean(cfStartDate);
	const hasDuration = Boolean(cfDurationWeeks && cfDurationWeeks > 0);
	if (hasStart && hasDuration) {
		const startD = parseLocalDate(cfStartDate);
		const endD   = startD ? addDays(startD, Math.round(cfDurationWeeks * 7)) : null;
		const tip    = startD && endD
			? `Start: ${fmtDate(startD)} · End: ${fmtDate(endD)} · ${cfDurationWeeks} weeks`
			: `Duration: ${cfDurationWeeks} weeks`;
		return <StatusDot label="Set" tooltip={tip} />;
	}
	if (hasStart || hasDuration) {
		const missing = !hasStart ? "start date" : "duration";
		return <StatusDot label="Partial" tooltip={`Missing ${missing}`} />;
	}
	return <StatusDot label="Not set" tooltip="No schedule details in BQE" />;
};

const ContactCell = ({ hasRecentContact }) => {
	if (hasRecentContact === null || hasRecentContact === undefined)
		return <span className="phdMuted">-</span>;
	return hasRecentContact
		? <StatusDot color={COLORS.green} label="Recent" tooltip="Email or call logged in last 7 days" />
		: <StatusDot color={COLORS.red}   label="None"   tooltip="No contact in last 7 days" />;
};

const CompanyCamCell = ({ hasRecentPhotos, recentPhotoCount, allTimePhotoCount }) => {
	if (hasRecentPhotos === null || hasRecentPhotos === undefined)
		return <span className="phdMuted">-</span>;
	const recent  = recentPhotoCount ?? 0;
	const allTime = allTimePhotoCount ?? 0;
	const label   = `${recent} - ${allTime}`;
	const tooltip = `${recent} photos in the last 7 days / ${allTime} photos all time`;
	return hasRecentPhotos
		? <StatusDot color={COLORS.green}  label={label} tooltip={tooltip} />
		: <StatusDot color={COLORS.yellow} label={label} tooltip={tooltip} />;
};

const SKEL_NAME_W = [62, 78, 52, 84, 67, 57, 74, 46];
const SKEL_MGR_W  = [68, 82, 58, 76, 88, 62, 78, 70];

const SkeletonBar = ({ h, w }) => (
	<div className="phdSkeletonBar" style={{ height: h, width: w }} />
);

const SkeletonRow = ({ index }) => {
	const nw = `${SKEL_NAME_W[index % SKEL_NAME_W.length]}%`;
	const mw = `${SKEL_MGR_W[index  % SKEL_MGR_W.length]}%`;
	return (
		<div className="phdRow">
			<div className="phdColProject phdCellProject">
				<SkeletonBar h={12} w={nw} />
			</div>
			<div className="phdColManager phdCellManager">
				<SkeletonBar h={12} w={mw} />
			</div>
			<div className="phdColStatus phdCellStatus">
				<SkeletonBar h={20} w={50} />
			</div>
			<div className="phdColOic phdCellOic">
				<SkeletonBar h={12} w={26} />
			</div>
			<div className="phdColStartDate phdCellStartDate">
				<SkeletonBar h={12} w={72} />
			</div>
			{Array.from({ length: 9 }, (_, i) => (
				<div key={i} className="phdColIndicator phdCellIndicator">
					<SkeletonBar h={22} w={58} />
				</div>
			))}
		</div>
	);
};

const TimeEntriesCell = ({ hasRecentTimeEntries }) => {
	if (hasRecentTimeEntries === null || hasRecentTimeEntries === undefined)
		return <span className="phdMuted">-</span>;
	return hasRecentTimeEntries
		? <StatusDot color={COLORS.green} label="Active" tooltip="Time logged in the last 7 days" />
		: <StatusDot color={COLORS.red}   label="None"   tooltip="No time entries in the last 7 days" />;
};

const SEVERITY_COLOR = (sev) => {
	if (!sev) return "#7a8190";
	const s = sev.toLowerCase();
	if (["high", "critical", "severe", "error"].some(k => s.includes(k))) return COLORS.red;
	if (["medium", "moderate", "warning", "warn"].some(k => s.includes(k))) return COLORS.yellow;
	if (["low", "minor", "ok", "good", "info"].some(k => s.includes(k))) return COLORS.green;
	return "#7a8190";
};

const AiRiskCell = ({ severity, reason }) => {
	if (!severity) return <span className="phdMuted">-</span>;
	const inner = <span className="phdStatusLabel">{severity}</span>;
	return reason
		? <Tooltip title={reason} arrow><span style={{ cursor: "default" }}>{inner}</span></Tooltip>
		: inner;
};

// â"€â"€ Column filter config â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const COLOR_LABELS = { red: "Red", yellow: "Yellow", green: "Green", missing: "No data", yes: "Has amendments", no: "No amendments" };

const COL_AVAILABLE_COLORS = {
	budget:        ["red", "green", "missing"],
	budget_status: ["red", "green", "missing"],
	duration:      ["red", "yellow", "green"],
	invoices:      ["red", "yellow", "green"],
	contact:       ["red", "green", "missing"],
	companycam:    ["yellow", "green", "missing"],
	amendments:    ["yes", "no"],
	time_entries:  ["red", "green", "missing"],
	ai_risk:       ["red", "yellow", "green", "missing"],
};

const COL_COLOR_TOOLTIPS = {
	budget: {
		red:     "No budget has been set in BQE",
		green:   "A budget exists in BQE",
		missing: "Budget data is not available for this project",
	},
	budget_status: {
		red:     "Logged hours exceed the project budget",
		green:   "Logged hours are within the project budget",
		missing: "No budget or hours data to determine status",
	},
	duration: {
		red:    "Neither start date nor duration is set in BQE",
		yellow: "Only one of start date or duration is set in BQE",
		green:  "Start date and duration are set in BQE; end date is calculated",
	},
	invoices: {
		red:    "One or more invoices are overdue",
		yellow: "Invoices are pending but none are overdue",
		green:  "All invoices are paid, or there are no invoices",
	},
	contact: {
		red:     "No email or call logged in HubSpot in the last 7 days",
		green:   "An email or call was logged in HubSpot in the last 7 days",
		missing: "No HubSpot deal is linked to this project",
	},
	companycam: {
		yellow:  "No photos uploaded to CompanyCam in the last 7 days (7-day count / all-time count shown)",
		green:   "Photos uploaded to CompanyCam in the last 7 days (7-day count / all-time count shown)",
		missing: "No CompanyCam project is linked",
	},
	amendments: {
		yes: "This project has one or more submitted amendments",
		no:  "This project has no submitted amendments",
	},
	time_entries: {
		red:     "No time has been logged in BQE in the last 7 days",
		green:   "Time has been logged in BQE in the last 7 days",
		missing: "Time entry data is not available for this project",
	},
	ai_risk: {
		red:     "High or critical risk identified in the latest report",
		yellow:  "Medium risk identified in the latest report",
		green:   "Low risk identified in the latest report",
		missing: "No automated report exists for this project",
	},
};

// Returns the health color for a given row + column key, or null if indeterminate.
const getRowColor = (row, colKey, budgetsReady) => {
	switch (colKey) {
		case "budget":
			if (!budgetsReady || row.has_budget === null) return null;
			return row.has_budget ? "green" : "red";
		case "budget_status":
			if (!budgetsReady || !row.has_budget || row.budget_status == null || row.budget_status === "Unknown") return null;
			return row.budget_status === "Over Budget" ? "red" : "green";
		case "duration": {
			const hasStart = Boolean(row.cf_start_date);
			const hasDur   = Boolean(row.cf_duration_weeks && row.cf_duration_weeks > 0);
			if (hasStart && hasDur) return "green";
			if (hasStart || hasDur) return "yellow";
			return "red";
		}
		case "invoices":
			if (row.invoice_status === "overdue") return "red";
			if (row.invoice_status === "pending") return "yellow";
			return "green";
		case "contact":
			if (row.has_recent_contact === null || row.has_recent_contact === undefined) return null;
			return row.has_recent_contact ? "green" : "red";
		case "companycam":
			if (row.has_recent_photos === null || row.has_recent_photos === undefined) return null;
			return row.has_recent_photos ? "green" : "yellow";
		case "amendments":
			return row.amendment_submitted_count > 0 ? "yes" : "no";
		case "time_entries":
			if (row.has_recent_time_entries === null || row.has_recent_time_entries === undefined) return null;
			return row.has_recent_time_entries ? "green" : "red";
		case "ai_risk": {
			if (!row.ai_severity) return null;
			const s = row.ai_severity.toLowerCase();
			if (["high", "critical", "severe", "error"].some(k => s.includes(k))) return "red";
			if (["medium", "moderate", "warning", "warn"].some(k => s.includes(k))) return "yellow";
			if (["low", "minor", "ok", "good", "info"].some(k => s.includes(k))) return "green";
			return null;
		}
		default: return null;
	}
};

// â"€â"€ Main component â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€

const ProjectHealthDashboard = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name:  "Project Health Dashboard",
			employeeID: employeeID,
			notes:      "Accessed the Project Health Dashboard page",
		}).catch(() => {});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Data
	const [apiIndicators,      setApiIndicators]      = useState([]);
	const [apiActivity,      setApiActivity]      = useState(null);
	const [aiPeriodStart,    setAiPeriodStart]    = useState(null);
	const [aiPeriodEnd,      setAiPeriodEnd]      = useState(null);

	// Loading
	const [loadingIndicators, setLoadingIndicators] = useState(true);
	const [error, setError] = useState("");

	// Filters
	const [selOffices,   setSelOffices]   = useState([]);
	const [officeInput,  setOfficeInput]  = useState("");
	const [selManagers,  setSelManagers]  = useState([]);
	const [managerInput, setManagerInput] = useState("");
	const [selStatuses,  setSelStatuses]  = useState([]);
	const [statusInput,  setStatusInput]  = useState("");
	const [selInit,      setSelInit]      = useState(false);
	const [searchQuery,  setSearchQuery]  = useState("");
	const [colFilters,   setColFilters]   = useState({});
	const [colPopover,   setColPopover]   = useState(null); // { anchor: el, colKey: string }

	const activityPollRef = useRef(null);
	const fetchActivityRef = useRef(null);
	const lastHiddenRef   = useRef(null);

	const openColFilter = useCallback((e, colKey) => {
		setColPopover({ anchor: e.currentTarget, colKey });
	}, []);

	const closeColFilter = useCallback(() => setColPopover(null), []);

	const toggleColFilterColor = useCallback((colKey, color) => {
		setColFilters(prev => {
			const current = new Set(prev[colKey] || []);
			if (current.has(color)) current.delete(color);
			else current.add(color);
			if (current.size === 0) {
				const next = { ...prev };
				delete next[colKey];
				return next;
			}
			return { ...prev, [colKey]: [...current] };
		});
	}, []);

	const clearColFilter = useCallback((colKey) => {
		setColFilters(prev => {
			const next = { ...prev };
			delete next[colKey];
			return next;
		});
	}, []);

	// â"€â"€ Fetch indicators â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
	useEffect(() => {
		let cancelled = false;
		(async () => {
			setLoadingIndicators(true);
			try {
				const res = await getAPICall(instance, accounts[0], "/api/project-health/indicators");
				if (!cancelled) {
					setApiIndicators(res.data?.indicators || []);
					setAiPeriodStart(res.data?.ai_last_period_start || null);
					setAiPeriodEnd(res.data?.ai_last_period_end   || null);
				}
			} catch (err) {
				if (!cancelled) setError(err?.response?.data?.message || err?.message || "Failed to load health indicators.");
			} finally {
				if (!cancelled) setLoadingIndicators(false);
			}
		})();
		return () => { cancelled = true; };
	}, [instance, accounts]);

	// ─── Fetch activity (HubSpot contact + CompanyCam) - polls every hour ─────────
	const fetchActivity = useCallback(async () => {
		try {
			const res = await getAPICall(instance, accounts[0], "/api/project-health/activity");
			if (Array.isArray(res.data?.activity)) {
				const map = new Map();
				for (const item of res.data.activity) {
					if (item?.bqe_id) map.set(item.bqe_id, item);
				}
				setApiActivity(map);
			}
		} catch (err) {
			console.error("[project-health] activity:", err?.message);
		}
	}, [instance, accounts]);

	useEffect(() => {
		fetchActivityRef.current = fetchActivity;
		fetchActivity();
		activityPollRef.current = setInterval(fetchActivity, ACTIVITY_POLL_MS);
		return () => clearInterval(activityPollRef.current);
	}, [fetchActivity]);

	// Re-fetch when tab regains focus after being hidden 5+ minutes
	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				lastHiddenRef.current = Date.now();
			} else {
				const hiddenMs = lastHiddenRef.current ? Date.now() - lastHiddenRef.current : 0;
				lastHiddenRef.current = null;
				if (hiddenMs > 5 * 60 * 1000 && fetchActivityRef.current) {
					fetchActivityRef.current();
				}
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
	}, []);

	// â"€â"€ Derived: unique offices + managers from indicator data â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
	const allOffices = useMemo(
		() => [...new Set(apiIndicators.map(i => i.location).filter(Boolean))].sort(),
		[apiIndicators]
	);
	const allManagers = useMemo(
		() => [...new Set(apiIndicators.map(i => i.manager).filter(Boolean))].sort(),
		[apiIndicators]
	);
	const allStatuses = useMemo(
		() => [...new Set(apiIndicators.map(i => i.status).filter(Boolean))].sort(),
		[apiIndicators]
	);

	// Select all offices + managers + statuses once data loads
	useEffect(() => {
		if (!selInit && allOffices.length > 0) {
			setSelOffices(allOffices);
			setSelManagers(allManagers);
			setSelStatuses(allStatuses);
			setSelInit(true);
		}
	}, [allOffices, allManagers, allStatuses, selInit]);

	// â"€â"€ Budget lookup maps â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
	// Budget fields (has_budget, budget_hours, spent_hours, budget_status) arrive
	// directly on each indicator row — computed server-side in projectHealth.js.
	const allRows = useMemo(() => {
		const activityReady = apiActivity !== null;
		return apiIndicators.map(ind => {
			const activity = apiActivity?.get(ind.bqe_id);
			return {
				...ind,
				has_recent_contact:        activity?.has_recent_contact       ?? (activityReady ? false : null),
				has_recent_photos:         activity?.has_recent_photos        ?? (activityReady ? false : null),
				recent_photo_count:        activity?.recent_photo_count       ?? (activityReady ? 0 : null),
				all_time_photo_count:      activity?.all_time_photo_count     ?? (activityReady ? 0 : null),
				has_recent_time_entries:   activity?.has_recent_time_entries  ?? (activityReady ? false : null),
			};
		});
	}, [apiIndicators, apiActivity]);

	// â"€â"€ Filtered rows â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
	const filteredRows = useMemo(() => {
		if (selOffices.length === 0 || selManagers.length === 0 || selStatuses.length === 0) return [];
		const _budgetsReady    = !loadingIndicators;
		const activeColFilters = Object.entries(colFilters).filter(([, colors]) => colors && colors.length > 0);
		const q = searchQuery.trim().toLowerCase();
		return allRows.filter(row => {
			if (q && ![row.property_name, row.project_name, row.phase_name, row.display_name]
				.some(v => v && v.toLowerCase().includes(q))) return false;
			if (selOffices.length < allOffices.length && !selOffices.includes(row.location)) return false;
			if (selManagers.length < allManagers.length && !selManagers.includes(row.manager)) return false;
			if (selStatuses.length < allStatuses.length && !selStatuses.includes(row.status)) return false;
			for (const [colKey, colors] of activeColFilters) {
				const rc = getRowColor(row, colKey, _budgetsReady);
				if (!colors.includes(rc === null ? "missing" : rc)) return false;
			}
			return true;
		}).sort((a, b) => {
			const pa = (a.property_name || a.display_name || "").toLowerCase();
			const pb = (b.property_name || b.display_name || "").toLowerCase();
			if (pa < pb) return -1;
			if (pa > pb) return 1;
			const pja = (a.project_name || "").toLowerCase();
			const pjb = (b.project_name || "").toLowerCase();
			if (pja < pjb) return -1;
			if (pja > pjb) return 1;
			return (a.phase_name || "").toLowerCase().localeCompare((b.phase_name || "").toLowerCase());
		});
	}, [allRows, selOffices, allOffices, selManagers, allManagers, selStatuses, allStatuses, searchQuery, colFilters, loadingIndicators]);

	const isLoading    = loadingIndicators;
	const budgetsReady = !loadingIndicators;
	const projectViewerUrl = (bqeId) => `/projects/viewer?recordId=${encodeURIComponent(bqeId)}`;

	const aiPeriodLabel = useMemo(() => {
		if (!aiPeriodEnd) return null;
		const fmt = (str) => {
			const d = new Date(`${str}T00:00:00`);
			return isNaN(d.getTime()) ? str : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
		};
		if (aiPeriodStart && aiPeriodStart !== aiPeriodEnd) {
			const startD = new Date(`${aiPeriodStart}T00:00:00`);
			const endD   = new Date(`${aiPeriodEnd}T00:00:00`);
			const year   = isNaN(endD.getTime()) ? "" : `, ${endD.getFullYear()}`;
			// Same month: "Jan 1 - 7, 2025" - different month: "Dec 28 - Jan 3, 2025"
			if (!isNaN(startD.getTime()) && startD.getMonth() === endD.getMonth() && startD.getFullYear() === endD.getFullYear()) {
				return `${fmt(aiPeriodStart)} - ${endD.getDate()}${year}`;
			}
			return `${fmt(aiPeriodStart)} - ${fmt(aiPeriodEnd)}${year}`;
		}
		const d = new Date(`${aiPeriodEnd}T00:00:00`);
		return isNaN(d.getTime()) ? aiPeriodEnd : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
	}, [aiPeriodStart, aiPeriodEnd]);

	// â"€â"€ Column header helper â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
	const renderIndicatorHeader = (colKey, label, tooltipText, extra = null) => {
		const activeColors = colFilters[colKey] || [];
		const isFiltered   = activeColors.length > 0;
		const isOpen       = colPopover?.colKey === colKey;
		const inner = (
			<div
				className={`phdColIndicator phdColHeaderStack phdColHeaderClickable${isFiltered ? " phdColHeaderFiltered" : ""}${isOpen ? " phdColHeaderActive" : ""}`}
				onClick={(e) => openColFilter(e, colKey)}
			>
				<span>{label}</span>
				{isFiltered && (
					<span className="phdColFilterDots">
						{activeColors.map(c =>
							c === "yes" ? <CheckIcon key={c} style={{ fontSize: 11, color: AMEND_GRAY }} />
							: c === "no"  ? <CloseIcon key={c} style={{ fontSize: 11, color: AMEND_GRAY }} />
							: <span key={c} className="phdFilterDot" style={{ background: COLORS[c] }} />
						)}
					</span>
				)}
				{extra}
			</div>
		);
		return tooltipText
			? <Tooltip title={isOpen ? "" : tooltipText} arrow>{inner}</Tooltip>
			: inner;
	};

	// â"€â"€ Render â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
	return (
		<div className="ProjectHealthDashboard">

			{/* â"€â"€ Page header â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
			<div className="phdPageHeader">
				<h1 className="phdTitle">Project Health Dashboard</h1>
				<div style={{ flex: 1 }} />
				<Tooltip
					title="Budget hours are synced from BQE by an hourly audit. Contact and CompanyCam data refreshes every hour. Information may appear out of date."
					placement="bottom"
					arrow
				>
					<span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "#7a8190", whiteSpace: "nowrap", cursor: "default" }}>
						<InfoOutlinedIcon style={{ fontSize: 15, color: "#9ca3af" }} />
						Data updates automatically
					</span>
				</Tooltip>
			</div>

			{/* â"€â"€ Filter bar â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
			<div className="phdFilterBar">
				<TextField
					size="small"
					placeholder="Search projects..."
					value={searchQuery}
					onChange={e => setSearchQuery(e.target.value)}
					sx={{ width: 220 }}
				/>
				<Autocomplete
					multiple
					disableCloseOnSelect
					size="small"
					options={[OFFICE_ALL, ...allOffices]}
					value={selOffices}
					onChange={(_, newValue) => {
						if (newValue.includes(OFFICE_ALL)) {
							setSelOffices(selOffices.length === allOffices.length ? [] : [...allOffices]);
						} else {
							setSelOffices(newValue);
						}
					}}
					inputValue={officeInput}
					onInputChange={(_, v) => setOfficeInput(v)}
					filterOptions={(_, { inputValue }) => {
						const q = inputValue.toLowerCase();
						return [OFFICE_ALL, ...allOffices.filter(o => o.toLowerCase().includes(q))];
					}}
					getOptionLabel={o => (o === OFFICE_ALL ? "Select All" : o)}
					renderOption={(props, option, { selected }) => {
						if (option === OFFICE_ALL) {
							const allSel = selOffices.length === allOffices.length;
							return (
								<li {...props} key={OFFICE_ALL}>
									<Checkbox indeterminate={selOffices.length > 0 && !allSel} checked={allSel} size="small" sx={{ mr: 1 }} />
									<span style={{ fontSize: 13, fontWeight: 500 }}>Select All</span>
								</li>
							);
						}
						return (
							<li {...props}>
								<Checkbox
									icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
									checkedIcon={<CheckBoxIcon fontSize="small" />}
									checked={selected}
									size="small"
									sx={{ mr: 1 }}
								/>
								{option}
							</li>
						);
					}}
					renderTags={value => officeInput ? null : (
						<span style={{ fontSize: 13, paddingLeft: 2, whiteSpace: "nowrap", pointerEvents: "none" }}>
							{value.length === allOffices.length ? "All Offices" : value.join(", ")}
						</span>
					)}
					renderInput={params => <TextField {...params} placeholder={selOffices.length === 0 ? "Offices" : ""} />}
					sx={{ width: 250, "& .MuiInputBase-root": { flexWrap: "nowrap" }, "& .MuiAutocomplete-input": { minWidth: "0 !important" } }}
				/>

				<Autocomplete
					multiple
					disableCloseOnSelect
					size="small"
					options={[MANAGER_ALL, ...allManagers]}
					value={selManagers}
					onChange={(_, newValue) => {
						if (newValue.includes(MANAGER_ALL)) {
							setSelManagers(selManagers.length === allManagers.length ? [] : [...allManagers]);
						} else {
							setSelManagers(newValue);
						}
					}}
					inputValue={managerInput}
					onInputChange={(_, v) => setManagerInput(v)}
					filterOptions={(_, { inputValue }) => {
						const q = inputValue.toLowerCase();
						return [MANAGER_ALL, ...allManagers.filter(m => m.toLowerCase().includes(q))];
					}}
					getOptionLabel={o => (o === MANAGER_ALL ? "Select All" : o)}
					renderOption={(props, option, { selected }) => {
						if (option === MANAGER_ALL) {
							const allSel = selManagers.length === allManagers.length;
							return (
								<li {...props} key={MANAGER_ALL}>
									<Checkbox indeterminate={selManagers.length > 0 && !allSel} checked={allSel} size="small" sx={{ mr: 1 }} />
									<span style={{ fontSize: 13, fontWeight: 500 }}>Select All</span>
								</li>
							);
						}
						return (
							<li {...props}>
								<Checkbox
									icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
									checkedIcon={<CheckBoxIcon fontSize="small" />}
									checked={selected}
									size="small"
									sx={{ mr: 1 }}
								/>
								{option}
							</li>
						);
					}}
					renderTags={value => managerInput ? null : (
						<span style={{ fontSize: 13, paddingLeft: 2, whiteSpace: "nowrap", pointerEvents: "none" }}>
							{value.length === allManagers.length ? "All Project Managers" : value.join(", ")}
						</span>
					)}
					renderInput={params => <TextField {...params} placeholder={selManagers.length === 0 ? "Managers" : ""} />}
					sx={{ width: 250, "& .MuiInputBase-root": { flexWrap: "nowrap" }, "& .MuiAutocomplete-input": { minWidth: "0 !important" } }}
				/>

				<Autocomplete
					multiple
					disableCloseOnSelect
					size="small"
					options={[STATUS_ALL, ...allStatuses]}
					value={selStatuses}
					onChange={(_, newValue) => {
						if (newValue.includes(STATUS_ALL)) {
							setSelStatuses(selStatuses.length === allStatuses.length ? [] : [...allStatuses]);
						} else {
							setSelStatuses(newValue);
						}
					}}
					inputValue={statusInput}
					onInputChange={(_, v) => setStatusInput(v)}
					filterOptions={(_, { inputValue }) => {
						const q = inputValue.toLowerCase();
						return [STATUS_ALL, ...allStatuses.filter(s => s.toLowerCase().includes(q))];
					}}
					getOptionLabel={o => (o === STATUS_ALL ? "Select All" : o)}
					renderOption={(props, option, { selected }) => {
						if (option === STATUS_ALL) {
							const allSel = selStatuses.length === allStatuses.length;
							return (
								<li {...props} key={STATUS_ALL}>
									<Checkbox indeterminate={selStatuses.length > 0 && !allSel} checked={allSel} size="small" sx={{ mr: 1 }} />
									<span style={{ fontSize: 13, fontWeight: 500 }}>Select All</span>
								</li>
							);
						}
						return (
							<li {...props}>
								<Checkbox
									icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
									checkedIcon={<CheckBoxIcon fontSize="small" />}
									checked={selected}
									size="small"
									sx={{ mr: 1 }}
								/>
								{option}
							</li>
						);
					}}
					renderTags={value => statusInput ? null : (
						<span style={{ fontSize: 13, paddingLeft: 2, whiteSpace: "nowrap", pointerEvents: "none" }}>
							{value.length === allStatuses.length ? "All Statuses" : value.join(", ")}
						</span>
					)}
					renderInput={params => <TextField {...params} placeholder={selStatuses.length === 0 ? "Status" : ""} />}
					sx={{ width: 200, "& .MuiInputBase-root": { flexWrap: "nowrap" }, "& .MuiAutocomplete-input": { minWidth: "0 !important" } }}
				/>

				<div style={{ flex: 1 }} />

				{!isLoading && (
					<span style={{ fontSize: 13, color: "#7a8190" }}>
						{filteredRows.length} of {allRows.length} project{allRows.length !== 1 ? "s" : ""}
					</span>
				)}
			</div>

			{/* â"€â"€ Table â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
			<div className="phdChart">
				{error ? (
					<div style={{ padding: "40px 24px", color: "#c62828", fontSize: 13 }}>{error}</div>
				) : (selOffices.length === 0 || selManagers.length === 0 || selStatuses.length === 0) && !isLoading ? (
					<div style={{ padding: "40px 24px", color: "#7a8190", fontSize: 13 }}>
						Select at least one office, manager, and status to view project health data.
					</div>
				) : (
					<div className="phdTableWrap">
						<div className="phdTableBody">
						<div className="phdTableInner">
						{/* Column headers */}
						<div className="phdColHeader">
							<div className="phdColProject">PROJECT</div>
							<div className="phdColManager">PROJECT MANAGER</div>
							<div className="phdColStatus">STATUS</div>
							<div className="phdColOic">OIC</div>
							<div className="phdColStartDate">START DATE</div>
							{renderIndicatorHeader("budget", "BUDGET", "Whether a budget has been created in BQE")}
							{renderIndicatorHeader("budget_status", "BUDGET STATUS", "All logged time (at budget rates) plus posted expenses, compared against the budget — same basis as BQE budget spent")}
							{renderIndicatorHeader("duration", "SCHEDULE", "Schedule details set in BQE")}
							{renderIndicatorHeader("amendments", "AMENDMENTS", "Amendments on this project")}
							{renderIndicatorHeader("invoices", "INVOICES", "Invoice status from BQE")}
							{renderIndicatorHeader("contact", "CONTACT", "Recent HubSpot contact activity")}
							{renderIndicatorHeader("companycam", "COMPANYCAM", "CompanyCam photo count: last 7 days - all time")}
							{renderIndicatorHeader("time_entries", "TIME ENTRIES", "Time logged in BQE in the last 7 days")}
							{renderIndicatorHeader(
								"ai_risk",
								"AI - RISK",
								"AI-assessed risk from the latest weekly reporting period. Information may appear outdated or inaccurate. Please double check all AI generated information.",
								aiPeriodLabel ? <span className="phdColHeaderSub">{aiPeriodLabel}</span> : null
							)}
						</div>

						{/* Skeleton while loading, empty state, or real rows */}
						{isLoading && allRows.length === 0 ? (
							Array.from({ length: 8 }, (_, i) => <SkeletonRow key={i} index={i} />)
						) : filteredRows.length === 0 ? (
							<div style={{ padding: "40px 24px", color: "#7a8190", fontSize: 13 }}>
								No projects match the current filters.
							</div>
						) : null}
						{(!isLoading || allRows.length > 0) && filteredRows.map(row => {
								const cellBg = (colKey) => {
									const c = getRowColor(row, colKey, budgetsReady);
									return c && BG_COLORS[COLORS[c]] ? { background: BG_COLORS[COLORS[c]] } : {};
								};
								return (
								<div key={row.bqe_id || row.record_id} className="phdRow">
									{/* Project name */}
									<div className="phdColProject phdCellProject">
										<Tooltip
											title={[row.property_name, row.project_name, row.phase_name].filter(Boolean).join(" · ")}
											placement="right"
											arrow
											enterDelay={400}
										>
											<a
												className="phdProjectLink"
												href={row.bqe_id ? projectViewerUrl(row.bqe_id) : undefined}
												target="_blank"
												rel="noreferrer"
											>
												<span className="phdProjectPrimary">
													{[row.property_name, row.project_name, row.phase_name].filter(Boolean).join(" · ")}
												</span>
												{row.bqe_id && <LaunchIcon className="phdLaunchIcon" fontSize="inherit" />}
											</a>
										</Tooltip>
									</div>

									{/* Project Manager */}
									<div className="phdColManager phdCellManager">
										{row.manager || <span className="phdMuted">-</span>}
									</div>

									{/* Status */}
									<div className="phdColStatus phdCellStatus">
										{row.status ? (
											<span
												className={`phdStatusChip${
													row.status.trim().toLowerCase() === "on hold" ? " onHold" : ""
												}`}
											>
												{row.status}
											</span>
										) : (
											<span className="phdMuted">-</span>
										)}
									</div>

									{/* OIC */}
									<div className="phdColOic phdCellOic">
										{row.location || <span className="phdMuted">-</span>}
									</div>

								{/* Start Date */}
								<div className="phdColStartDate phdCellStartDate">
									{(() => {
										const dt = parseLocalDate(row.cf_start_date);
										return dt ? fmtDate(dt) : <span className="phdMuted">-</span>;
									})()}
								</div>

									{/* Budget */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("budget")}>
										<BudgetCell hasBudget={row.has_budget} loading={!budgetsReady} />
									</div>

									{/* Budget Status */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("budget_status")}>
										<BudgetStatusCell
											hasBudget={row.has_budget}
											budgetHours={row.budget_hours}
											spentHours={row.spent_hours}
											budgetAmount={row.budget_amount}
											spentAmount={row.spent_amount}
											status={row.budget_status}
											varianceAmount={row.budget_variance_amount}
											loading={!budgetsReady}
										/>
									</div>

									{/* Schedule */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("duration")}>
										<DurationCell cfStartDate={row.cf_start_date} cfDurationWeeks={row.cf_duration_weeks} />
									</div>

									{/* Amendments */}
									<div className="phdColIndicator phdCellIndicator">
										<AmendmentsCell submittedCount={row.amendment_submitted_count ?? 0} pendingCount={row.amendment_pending_count ?? 0} />
									</div>

									{/* Invoices */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("invoices")}>
										<InvoicesCell
											status={row.invoice_status}
											overdueCount={row.invoice_overdue_count}
											unpaidCount={row.invoice_unpaid_count}
											draftCount={row.invoice_draft_count}
										/>
									</div>

									{/* Contact */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("contact")}>
										<ContactCell hasRecentContact={row.has_recent_contact} />
									</div>

									{/* CompanyCam */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("companycam")}>
										<CompanyCamCell
											hasRecentPhotos={row.has_recent_photos}
											recentPhotoCount={row.recent_photo_count}
											allTimePhotoCount={row.all_time_photo_count}
										/>
									</div>

									{/* Time Entries */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("time_entries")}>
										<TimeEntriesCell hasRecentTimeEntries={row.has_recent_time_entries} />
									</div>

									{/* AI Risk */}
									<div className="phdColIndicator phdCellIndicator" style={cellBg("ai_risk")}>
										<AiRiskCell severity={row.ai_severity} reason={row.ai_reason} />
									</div>
								</div>
								);
							})}
						</div>
						</div>
					</div>
				)}
			</div>

			{/* â"€â"€ Column filter popover â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
			<Popover
				open={Boolean(colPopover)}
				anchorEl={colPopover?.anchor}
				onClose={closeColFilter}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				transformOrigin={{ vertical: "top", horizontal: "center" }}
				disableRestoreFocus
			>
				{colPopover && (
					<div className="phdColFilterPopover">
						<div className="phdColFilterPopoverTitle">Filter by status</div>
						{(COL_AVAILABLE_COLORS[colPopover.colKey] || []).map(c => {
							const optTip = COL_COLOR_TOOLTIPS[colPopover.colKey]?.[c] || "";
							return (
								<Tooltip key={c} title={optTip} placement="right" arrow enterDelay={400}>
									<label className="phdColFilterOption">
										<Checkbox
											size="small"
											checked={(colFilters[colPopover.colKey] || []).includes(c)}
											onChange={() => toggleColFilterColor(colPopover.colKey, c)}
											sx={{ p: "4px", mr: "6px" }}
										/>
										{c === "yes"     ? <CheckIcon style={{ fontSize: 16, color: AMEND_GRAY, marginRight: 6 }} />
											: c === "no"      ? <CloseIcon style={{ fontSize: 16, color: AMEND_GRAY, marginRight: 6 }} />
											: c === "missing" ? <span style={{ color: COLORS.missing, marginRight: 6, fontSize: 14, lineHeight: 1, fontWeight: 600 }}>-</span>
											: <span className="phdDot" style={{ background: COLORS[c], marginRight: 6, width: 9, height: 9 }} />
										}
										<span>{COLOR_LABELS[c]}</span>
									</label>
								</Tooltip>
							);
						})}
						{(colFilters[colPopover.colKey] || []).length > 0 && (
							<button
								className="phdColFilterClear"
								onClick={() => clearColFilter(colPopover.colKey)}
							>
								Clear filter
							</button>
						)}
					</div>
				)}
			</Popover>
		</div>
	);
};

export default ProjectHealthDashboard;
